<script setup>
import { computed } from 'vue';

// Tabla del rediseño: cabecera fija, filas con separador y hover, celdas de
// borde con el margen de la tarjeta. Contactos, Empresas y Tareas repetían
// esa misma envoltura con las clases copiadas a mano; aquí se define una vez
// y cada pantalla aporta sólo el contenido de sus celdas, por el slot
// `cell-<clave de la columna>`.
//
// Columnas: { key, label, width?, align?, headerClass?, cellClass? }, donde
// `width` es una clase de Tailwind (`w-[24%]`). Sin `width` la columna se
// reparte el espacio que sobra.
const props = defineProps({
  columns: { type: Array, required: true },
  rows: { type: Array, default: () => [] },
  // Filas agrupadas: [{ key, label, dot?, count?, rows }]. Cuando se pasan,
  // `rows` se ignora y cada grupo rinde su propio <tbody> con su cabecera.
  groups: { type: Array, default: null },
  rowKey: { type: String, default: 'id' },
  // Columna inicial de casillas de selección.
  selectable: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
  clickable: { type: Boolean, default: false },
  // Las filas de una línea respiran menos que las que llevan dos.
  density: {
    type: String,
    default: 'compact',
    validator: value => ['compact', 'regular'].includes(value),
  },
  // Clases extra por fila, p. ej. para marcar la seleccionada.
  rowClass: { type: Function, default: null },
});

const emit = defineEmits(['rowClick', 'toggleRow', 'toggleAll']);

const PAD_Y = { compact: 'py-2.5', regular: 'py-3' };
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
const CHECKBOX_CLASS =
  'size-4 rounded border-n-slate-7 text-n-brand focus:ring-n-brand/40 cursor-pointer';

const padY = computed(() => PAD_Y[props.density] || PAD_Y.compact);

// La primera y la última columna se pegan al borde de la tarjeta; la
// casilla de selección, cuando está, es quien ocupa ese borde inicial.
const edgeClass = index => {
  const isFirst = index === 0 && !props.selectable;
  const isLast = index === props.columns.length - 1;
  return [
    isFirst ? 'ltr:pl-5 rtl:pr-5 ltr:pr-3 rtl:pl-3' : '',
    isLast ? 'ltr:pr-5 rtl:pl-5 ltr:pl-3 rtl:pr-3' : '',
    !isFirst && !isLast ? 'px-3' : '',
  ];
};

const headerClass = (column, index) => [
  padY.value,
  ALIGN[column.align] || ALIGN.left,
  'text-xs font-medium uppercase tracking-wide text-n-slate-10 truncate',
  column.width,
  ...edgeClass(index),
  column.headerClass,
];

const cellClass = (column, index) => [
  padY.value,
  'align-middle',
  ALIGN[column.align] || ALIGN.left,
  ...edgeClass(index),
  column.cellClass,
];

const bodies = computed(() =>
  props.groups
    ? props.groups.map(group => ({ ...group, rows: group.rows || [] }))
    : [{ key: '__all__', rows: props.rows }]
);

const colSpan = computed(
  () => props.columns.length + (props.selectable ? 1 : 0)
);

const selectedSet = computed(() => new Set(props.selectedIds || []));
const isSelected = row => selectedSet.value.has(row[props.rowKey]);
const allSelected = computed(
  () => props.rows.length > 0 && props.rows.every(isSelected)
);
const someSelected = computed(
  () => !allSelected.value && props.rows.some(isSelected)
);

const onRowClick = row => {
  if (props.clickable) emit('rowClick', row);
};
</script>

<template>
  <table class="w-full table-fixed border-collapse">
    <thead>
      <tr class="border-b border-n-weak bg-n-alpha-1 sticky top-0 z-[1]">
        <th v-if="selectable" class="w-10 ltr:pl-5 rtl:pr-5" :class="padY">
          <input
            type="checkbox"
            :class="CHECKBOX_CLASS"
            :checked="allSelected"
            :indeterminate.prop="someSelected"
            @change="emit('toggleAll', $event.target.checked)"
          />
        </th>
        <th
          v-for="(column, index) in columns"
          :key="column.key"
          :class="headerClass(column, index)"
        >
          {{ column.label }}
        </th>
      </tr>
    </thead>
    <tbody v-for="body in bodies" :key="body.key">
      <tr v-if="body.label">
        <td
          :colspan="colSpan"
          class="py-2 ltr:pl-5 rtl:pr-5 ltr:pr-3 rtl:pl-3 bg-n-alpha-1/60 border-b border-n-weak"
        >
          <slot name="group" :group="body">
            <div class="flex items-center gap-2">
              <span
                v-if="body.dot"
                class="size-1.5 rounded-full flex-shrink-0"
                :class="body.dot"
              />
              <span class="text-xs font-semibold text-n-slate-12">
                {{ body.label }}
              </span>
              <span class="text-xs text-n-slate-9">
                {{ body.count ?? body.rows.length }}
              </span>
            </div>
          </slot>
        </td>
      </tr>
      <tr
        v-for="row in body.rows"
        :key="row[rowKey]"
        class="border-b border-n-weak last:border-b-0 transition-colors hover:bg-n-alpha-1 group/row"
        :class="[
          clickable ? 'cursor-pointer' : '',
          rowClass ? rowClass(row) : '',
        ]"
        @click="onRowClick(row)"
      >
        <td
          v-if="selectable"
          class="ltr:pl-5 rtl:pr-5 align-middle"
          :class="padY"
          @click.stop
        >
          <input
            type="checkbox"
            :class="CHECKBOX_CLASS"
            :checked="isSelected(row)"
            @change="
              emit('toggleRow', {
                id: row[rowKey],
                value: $event.target.checked,
              })
            "
          />
        </td>
        <td
          v-for="(column, index) in columns"
          :key="column.key"
          :class="cellClass(column, index)"
        >
          <slot :name="`cell-${column.key}`" :row="row" :column="column" />
        </td>
      </tr>
    </tbody>
  </table>
</template>
